import React, { useState, useEffect, useCallback } from 'react';
import { AUTH_EXPIRED_EVENT } from '../api/authFetch';
import { refreshProxyCredential } from '../api/proxyAuth';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

// SessionExpiredModal — the in-app re-authentication surface for an ACTIVE
// dashboard whose backend token has expired (idle past the 8h wall).
//
// Why an overlay instead of routing back to LoginPage: App.js gates the whole
// dashboard on TrafficContext.sessionId, and LoginPage owns its own auth state.
// Sending the user back there would unmount MainApp — killing the live proxy
// WebSocket and dropping the captured-traffic view. This modal instead renders
// ON TOP of the still-mounted dashboard: TrafficContext, the WS, groupedResults,
// and any selected rows all survive. After a successful re-login the overlay
// simply dismisses and the user is exactly where they were — they retry the
// action (e.g. the HAR save) with the fresh token.
//
// Listens for the shared `auth:expired` window event that the api clients fire
// on any backend 401 (see api/authFetch.js), so EVERY authed call degrades the
// same graceful way — not just the Save button.
export default function SessionExpiredModal() {
    const [open, setOpen]         = useState(false);
    const [username, setUsername] = useState(() => {
        try { return localStorage.getItem('auth_user') || ''; } catch { return ''; }
    });
    const [password, setPassword] = useState('');
    const [error, setError]       = useState('');
    const [loading, setLoading]   = useState(false);

    useEffect(() => {
        const onExpired = () => {
            setPassword('');
            setError('');
            try { setUsername(localStorage.getItem('auth_user') || username); } catch {}
            setOpen(true);
        };
        window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
        return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = useCallback(async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: username.trim(), password }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.ok && data.token) {
                // Persist ONLY the token/user — never touch sessionId / groupedResults.
                try {
                    localStorage.setItem('auth_token', data.token);
                    localStorage.setItem('auth_user', data.username);
                } catch {}
                // Re-prime the proxy credential cache after a re-login the same
                // way LoginPage does (fire-and-forget, docs/proxy-credential-spec.md).
                refreshProxyCredential();
                setPassword('');
                setOpen(false);
            } else {
                setError(data.error === 'invalid_credentials'
                    ? 'Invalid username or password'
                    : `Sign-in failed (${res.status})`);
            }
        } catch (err) {
            setError(`Connection error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [username, password]);

    if (!open) return null;

    return (
        <div style={S.backdrop} role="dialog" aria-modal="true" aria-label="Session expired — sign in again">
            <div style={S.card}>
                <div style={{ fontSize: 30, marginBottom: 6 }}>🔒</div>
                <h2 style={S.title}>Session expired</h2>
                <p style={S.subtitle}>
                    Your sign-in timed out. Log back in to continue — your captured
                    session is preserved. After signing in, retry your last action.
                </p>
                <form onSubmit={submit}>
                    <input
                        type="text"
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                        placeholder="Username"
                        autoComplete="username"
                        style={S.input}
                    />
                    <input
                        type="password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="Password"
                        autoFocus
                        autoComplete="current-password"
                        style={{ ...S.input, marginTop: 10 }}
                    />
                    {error && <div style={S.error}>{error}</div>}
                    <button type="submit" disabled={loading || !username || !password} style={S.btn}>
                        {loading ? 'Signing in…' : 'Sign In & Continue'}
                    </button>
                </form>
            </div>
        </div>
    );
}

const S = {
    backdrop: {
        position: 'fixed', inset: 0, zIndex: 100000,
        background: 'rgba(10,14,24,0.72)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    },
    card: {
        background: '#1a1f2e', color: 'white',
        border: '1px solid rgba(255,255,255,0.14)', borderRadius: 16,
        padding: '30px 34px', width: '100%', maxWidth: 360, textAlign: 'center',
        boxShadow: '0 12px 48px rgba(0,0,0,0.5)',
    },
    title: { margin: '0 0 8px', fontSize: 20, fontWeight: 700 },
    subtitle: { color: 'rgba(255,255,255,0.55)', fontSize: 12.5, lineHeight: 1.5, marginBottom: 20 },
    input: {
        width: '100%', padding: '11px 14px', borderRadius: 8,
        border: '1px solid rgba(255,255,255,0.15)',
        background: 'rgba(0,0,0,0.25)', color: 'white',
        fontSize: 15, outline: 'none', boxSizing: 'border-box',
    },
    error: { color: '#f87171', fontSize: 12, margin: '8px 0', textAlign: 'left' },
    btn: {
        width: '100%', padding: '12px', marginTop: 14,
        background: '#007aff', color: 'white', border: 'none', borderRadius: 8,
        fontSize: 15, fontWeight: 600, cursor: 'pointer',
    },
};
